"use client"

import logger from "@/lib/logger/logger-init"
import React, { createContext, useContext, useEffect, useRef, useState } from "react"

const AccordionContext = createContext<{
    isOpen: boolean
    toggle: () => void
} | null>(null)

type AccordionRootProps = {
    children: React.ReactNode
    initalState?: boolean
    hydratedState?: boolean
    className?: string
}

export const AccordionRoot = ({
    initalState = false,
    hydratedState = false,
    children,
    className
}: AccordionRootProps) => {
    const [isOpen, setIsOpen] = useState(initalState)

    useEffect(() => {
        setIsOpen(hydratedState)
    }, [hydratedState])

    const toggle = () => setIsOpen(prev => !prev)

    return (
        <AccordionContext.Provider value={{ isOpen, toggle }}>
            <div className={className}>{children}</div>
        </AccordionContext.Provider>
    )
}

// AccordionTrigger
type AccordionTriggerProps = {
    children: React.ReactNode
    className?: string
}

export const AccordionTrigger = ({ children, className }: AccordionTriggerProps) => {
    const context = useContext(AccordionContext)
    if (!context) {
        logger.error("AccordionTrigger must be used within an AccordionRoot")
        return null
    }

    const { isOpen, toggle } = context

    return (
        <button
            className={className}
            onClick={toggle}
            aria-expanded={isOpen}
            aria-controls="accordion-content"
            id="accordion-trigger"
        >
            {children}
        </button>
    )
}

//AccordionIcon
type AccordionIconProps = {
    openIcon?: React.ReactNode
    closeIcon?: React.ReactNode
    className?: string
}

export const AccordionIcon = ({ openIcon, closeIcon, className }: AccordionIconProps) => {
    const context = useContext(AccordionContext)
    if (!context) {
        logger.error("AccordionIcon must be used within an AccordionRoot")
        return null
    }

    const { isOpen } = context

    return <div className={className}>{isOpen ? openIcon : closeIcon}</div>
}

// AccordionContent
type AccordionContentProps = {
    children: React.ReactNode
    className?: string
}

export const AccordionContent = ({ children, className }: AccordionContentProps) => {
    const context = useContext(AccordionContext)

    // const { isOpen } = context
    const ref = useRef<HTMLDivElement | null>(null)
    const [maxHeight, setMaxHeight] = useState("0px")

    useEffect(() => {
        if (ref.current) {
            setMaxHeight(context?.isOpen ? `${ref.current.scrollHeight}px` : "0px")
        }
    }, [context?.isOpen])

    if (!context) {
        logger.error("AccordionContent must be used within an AccordionRoot")
        return null
    }

    return (
        <div
            ref={ref}
            className={`${className} transition-max-height duration-300 ease-in-out`}
            style={{
                maxHeight,
                overflow: "hidden"
            }}
            id="accordion-content"
            role="region"
            aria-labelledby="accordion-trigger"
        >
            {children}
        </div>
    )
}
